import { Component, Element, Host, Prop, State, Watch, h } from '@stencil/core';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { getIconUrl } from './zds-icon-collection';

const cache = new Map<string, string>();
const requests = new Map<string, Promise<string>>();

@Component({
	shadow: true,
	styleUrl: 'zds-icon.scss',
	tag: 'zds-icon',
})
export class ZdsIcon {
	@Element() hostElement: HTMLDivElement;

	@State() svgContent?: string;

	@Prop({ reflect: true }) alt: string;
	@Prop({ reflect: true }) library: string = 'mui';
	@Prop({ reflect: true }) name: string = '';
	@Prop({ reflect: true }) size: number | 'inherit' = 6;
	@Prop({ reflect: true }) sizingType: 'square' | 'height' | 'width' = 'square';

	@Watch('library')
	@Watch('name')
	onNameChange(newValue: string, currentValue: string) {
		if (currentValue === newValue) return;
		this.getIcon();
	}

	componentWillLoad() {
		this.getIcon();
	}

	/**
	 * Determines the url for the given icon name attribute and sets the
	 * 	svgContent state accordingly.
	 */
	getIcon() {
		const url = getIconUrl(this.library, this.name);
		if (!url) {
			// Image does not exist, use a placeholder instead
			cache.set(url, '<div class="zds-icon__missing"></div>');
			return;
		}

		this.fetchIcon(url)
			.then((result) => {
				this.svgContent = result;
			})
			.catch(() => {
				console.error(`icon '${this.name}' was not resolved`);
			});
	}

	/**
	 * Does the actual fetch of the given icon url. First checks whether the
	 * 	given icon url had been requested previously and returns that request
	 * 	instead.
	 */
	async fetchIcon(url: string): Promise<string> {
		// Avoid doing the same fetch call multiple times
		const pendingRequest = requests.get(url);
		if (pendingRequest) {
			return pendingRequest;
		}

		// We wrap the entire request + setting the cache in one promise,
		// otherwise the pending requests return too quickly before a cache value is set
		const promiseWrapper = new Promise<string>((resolve, reject) => {
			fetch(url)
				.then((response) => {
					if (response.ok) {
						return response.text();
					}
				})
				.then((result) => {
					this.svgContent = result;

					cache.set(url, this.svgContent);
					resolve(result);
				})
				.catch(reject);
		});

		// Set the request in our pending the requests still otherwise we get duplicate calls
		requests.set(url, promiseWrapper);
		return promiseWrapper;
	}

	render() {
		const iconClasses = generateCssClasses('zds-icon', {
			[`sizing-type-${this.sizingType}`]: true,
		});

		let styleCode: string;
		if (this.size !== undefined && this.size !== 'inherit') {
			styleCode = `
				:host([size='${this.size}']) {
					font-size: ${this.size * 4}px;
				}
			`;
		}

		return (
			<Host
				role={this.alt && 'img'}
				aria-hidden={!this.alt ? 'true' : 'false'}
				aria-label={this.alt}
				alt={this.alt}
			>
				<div
					class={iconClasses}
					innerHTML={this.svgContent ?? cache.get(getIconUrl(this.library, this.name))}
				></div>
				<style innerHTML={styleCode}></style>
			</Host>
		);
	}
}
